import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MdArrowBack, 
  MdSave, 
  MdPerson, 
  MdEuro, 
  MdCalendarToday, 
  MdPayment,
  MdReceipt,
  MdPrint,
  MdDownload,
  MdClose,
  MdCheckCircle
} from 'react-icons/md';
import { clientesAPI, recibosAPI, pagosAPI, formatDateForAPI, configuracionAPI } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const RegisterPaymentContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  span.required {
    color: ${props => props.theme.colors.danger};
  }
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  max-height: 280px;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 1000;
  margin-top: -1px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius.sm};
    
    &:hover {
      background: ${props => props.theme.colors.primary}50;
    }
  }
`;

const SuggestionItem = styled.div`
  padding: ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.primary}15;
  }

  ${props => props.selected && `
    background-color: ${props.theme.colors.primary}20;
  `}
`;

const SuggestionName = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const SuggestionDetails = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const NoResults = styled.div`
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
    outline: none;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: ${props => props.theme.colors.background};
  }
`;

const ReceiptInfoCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ReceiptInfoTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const ReceiptInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ReceiptInfoLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ReceiptInfoValue = styled.span`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const PaymentTypeButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const PaymentTypeButton = styled.button`
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  border: 2px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaymentTypeTitle = styled.span`
  font-size: ${props => props.theme.fontSizes.lg};
`;

const PaymentTypeDescription = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  opacity: 0.8;
`;

const SummaryCard = styled.div`
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.success};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SummaryTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
    padding-top: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.md};
    border-top: 2px solid ${props => props.theme.colors.border};
  }
`;

const SummaryLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const SummaryValue = styled.span`
  font-size: ${props => props.large ? props.theme.fontSizes.xxl : props.theme.fontSizes.md};
  color: ${props => props.large ? props.theme.colors.success : props.theme.colors.textPrimary};
  font-weight: ${props => props.large ? props.theme.fontWeights.bold : props.theme.fontWeights.semibold};
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 1;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.active ? props.theme.colors.primary : '#e5e7eb'};
  color: ${props => props.active ? 'white' : '#9ca3af'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.fontWeights.bold};
  font-size: ${props => props.theme.fontSizes.md};
  transition: all 0.3s ease;
`;

const StepLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 15px;
`;

const ClientCard = styled.div`
  padding: 15px;
  margin-bottom: 10px;
  background: white;
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : '#e5e7eb'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 6px rgba(99, 102, 241, 0.1);
    transform: translateY(-2px);
  }
`;

const ClientName = styled.div`
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const ClientInfo = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const ReciboCard = styled.div`
  padding: 15px;
  margin-bottom: 12px;
  background: white;
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : '#e5e7eb'};
  border-radius: 12px;
  transition: all 0.2s ease;
  
  ${props => !props.checkbox && `
    cursor: pointer;
    &:hover {
      border-color: ${props.theme.colors.primary};
      box-shadow: 0 4px 6px rgba(99, 102, 241, 0.1);
      transform: translateY(-2px);
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing.xl};
  padding-top: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const Button = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.primary ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.semibold};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.sm};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.lg};
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xxl};
  color: ${props => props.theme.colors.success};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};

  svg {
    font-size: 2rem;
  }
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: ${props => props.theme.transitions.fast};

  svg {
    font-size: 1.5rem;
  }

  &:hover {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const ReceiptDocument = styled.div`
  background: white;
  padding: ${props => props.theme.spacing.xxl};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 2px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media print {
    border: none;
    padding: 0;
  }
`;

const ReceiptHeader = styled.div`
  text-align: center;
  padding-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ReceiptTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.primary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

const ReceiptSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const ReceiptSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ReceiptSectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const ReceiptInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const InfoLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const TotalBox = styled.div`
  background: ${props => props.theme.colors.success}15;
  border: 2px solid ${props => props.theme.colors.success};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
`;

const TotalLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TotalAmount = styled.div`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.success};
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  border-top: 1px solid ${props => props.theme.colors.border};

  @media print {
    display: none;
  }
`;

// Confirmation Modal Styles
const ConfirmModalContent = styled(ModalContent)`
  max-width: 550px;
`;

const ConfirmModalTitle = styled(ModalTitle)`
  color: ${props => props.theme.colors.warning};
`;

const ConfirmModalBody = styled(ModalBody)`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xxl};
`;

const ConfirmMessage = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xl} 0;
  text-align: center;
`;

const ConfirmDetails = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ConfirmDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
    padding-top: ${props => props.theme.spacing.md};
    margin-top: ${props => props.theme.spacing.sm};
    font-weight: ${props => props.theme.fontWeights.semibold};
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const ConfirmDetailLabel = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const ConfirmDetailValue = styled.span`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ConfirmButtonPrimary = styled(ConfirmButton)`
  background-color: ${props => props.theme.colors.success};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.successDark || '#388e3c'};
  }
`;

const ConfirmButtonSecondary = styled(ConfirmButton)`
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.backgroundLight};
  }
`;

const RegisterPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const today = new Date().toISOString().split('T')[0];
  const preSelectedReciboId = location.state?.reciboId;
  const preSelectedClienteId = location.state?.clienteId;
  
  const [formData, setFormData] = useState({
    cliente: '',
    recibo: '',
    fecha: today,
    metodoPago: 'transferencia',
    numeroReferencia: '',
    montoPago: '',
    tipoPago: 'liquidar',
    notas: '',
    pagoMultiple: false
  });

  const [clientes, setClientes] = useState([]);
  const [clientesBusqueda, setClientesBusqueda] = useState('');
  const [recibosBusqueda, setRecibosBusqueda] = useState('');
  const [showClienteSuggestions, setShowClienteSuggestions] = useState(false);
  const [showReciboSuggestions, setShowReciboSuggestions] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [clientReceipts, setClientReceipts] = useState([]);
  const [selectedRecibos, setSelectedRecibos] = useState([]);
  const [recibosConMonto, setRecibosConMonto] = useState([]); // Ahora incluirá datos completos de pago
  const [showModal, setShowModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [config, setConfig] = useState(null);

  // Cargar configuración al montar el componente
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await configuracionAPI.getConfiguracion();
        setConfig(response.data.data);
      } catch (error) {
        console.error('Error al cargar configuración:', error);
      }
    };
    loadConfig();
  }, []);

  // Cargar clientes al montar el componente
  useEffect(() => {
    const loadClientes = async () => {
      try {
        // Obtener TODOS los clientes
        const response = await clientesAPI.getAll();
        const todosLosClientes = response.data.data || [];
        
        // Ordenar por nombre
        const clientesOrdenados = todosLosClientes.sort((a, b) => 
          a.nombre.localeCompare(b.nombre)
        );
        
        setClientes(clientesOrdenados);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
        setLoading(false);
      }
    };
    
    loadClientes();
  }, []);

  // Cargar recibo pre-seleccionado si viene desde otra página
  useEffect(() => {
    const loadPreSelectedRecibo = async () => {
      if (preSelectedReciboId && !loading) {
        try {
          const response = await recibosAPI.getById(preSelectedReciboId);
          const recibo = response.data.data;
          
          if (recibo && parseFloat(recibo.saldo_pendiente || 0) > 0) {
            // Seleccionar el cliente automáticamente
            setFormData(prev => ({
              ...prev,
              cliente: recibo.cliente_id.toString(),
              recibo: recibo.id.toString(),
              montoPago: recibo.saldo_pendiente.toString()
            }));
            setSelectedReceipt(recibo);
          }
        } catch (error) {
          console.error('Error al cargar recibo pre-seleccionado:', error);
        }
      }
    };
    
    loadPreSelectedRecibo();
  }, [preSelectedReciboId, loading]);

  // Pre-seleccionar cliente si viene desde el perfil del cliente
  useEffect(() => {
    if (preSelectedClienteId && !loading && !preSelectedReciboId && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === parseInt(preSelectedClienteId));
      if (cliente && !selectedClient) {
        console.log('Pre-seleccionando cliente:', cliente);
        setFormData(prev => ({
          ...prev,
          cliente: cliente.id.toString()
        }));
        setSelectedClient(cliente);
      }
    }
  }, [preSelectedClienteId, loading, clientes, preSelectedReciboId, selectedClient]);

  // Sincronizar selectedClient con formData.cliente
  useEffect(() => {
    if (formData.cliente && !selectedClient && clientes.length > 0) {
      const cliente = clientes.find(c => c.id === parseInt(formData.cliente));
      if (cliente) {
        console.log('Sincronizando selectedClient:', cliente);
        setSelectedClient(cliente);
      }
    }
  }, [formData.cliente, selectedClient, clientes]);

  useEffect(() => {
    const loadClientReceipts = async () => {
      if (formData.cliente) {
        try {
          // Obtener todos los recibos del cliente sin filtrar por estado específico
          const response = await recibosAPI.getByCliente(formData.cliente);
          const recibos = response.data.data || [];
          // Filtrar solo los que tienen saldo pendiente y no están pagados o cancelados
          const recibosFiltrados = recibos.filter(r => 
            parseFloat(r.saldo_pendiente || 0) > 0 && 
            r.estado !== 'pagado' && 
            r.estado !== 'cancelado'
          );
          setClientReceipts(recibosFiltrados);
          
          // Solo resetear si NO hay un recibo pre-seleccionado
          if (!preSelectedReciboId) {
            setFormData(prev => ({ ...prev, recibo: '', montoPago: '' }));
            setSelectedReceipt(null);
          }
        } catch (error) {
          console.error('Error al cargar recibos:', error);
          setClientReceipts([]);
        }
      } else {
        setClientReceipts([]);
        if (!preSelectedReciboId) {
          setSelectedReceipt(null);
        }
      }
    };
    
    loadClientReceipts();
  }, [formData.cliente]);

  useEffect(() => {
    if (formData.recibo && clientReceipts.length > 0) {
      const receipt = clientReceipts.find(r => r.id === parseInt(formData.recibo));
      if (receipt) {
        setSelectedReceipt(receipt);
        
        // Actualizar monto solo si es liquidar o si no hay monto establecido
        if (formData.tipoPago === 'liquidar' && !preSelectedReciboId) {
          setFormData(prev => ({ ...prev, montoPago: receipt.saldo_pendiente.toString() }));
        }
      }
    } else if (!formData.recibo && !preSelectedReciboId) {
      setSelectedReceipt(null);
    }
  }, [formData.recibo, clientReceipts, formData.tipoPago]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-autocomplete]')) {
        setShowClienteSuggestions(false);
        setShowReciboSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      tipoPago: type,
      montoPago: type === 'liquidar' && selectedReceipt ? selectedReceipt.saldo_pendiente.toString() : ''
    }));
  };

  // Funciones para autocompletado de cliente
  const handleClienteSearchChange = (e) => {
    const value = e.target.value;
    setClientesBusqueda(value);
    setShowClienteSuggestions(value.length > 0);
    
    if (value.length === 0) {
      setFormData(prev => ({ ...prev, cliente: '', recibo: '' }));
      setSelectedClient(null);
      setClientReceipts([]);
      setSelectedRecibos([]);
      setRecibosConMonto([]);
    }
  };

  const handleClienteSelectFromSuggestions = (cliente) => {
    setClientesBusqueda(cliente.nombre);
    setFormData(prev => ({ ...prev, cliente: cliente.id.toString(), recibo: '' }));
    setSelectedClient(cliente);
    setSelectedRecibos([]);
    setRecibosConMonto([]);
    setRecibosBusqueda('');
    setShowClienteSuggestions(false);
  };

  const getFilteredClientes = () => {
    if (!clientesBusqueda) return [];
    
    const searchLower = clientesBusqueda.toLowerCase();
    return clientes.filter(cliente => 
      cliente.nombre.toLowerCase().includes(searchLower) ||
      (cliente.rfc && cliente.rfc.toLowerCase().includes(searchLower)) ||
      (cliente.email && cliente.email.toLowerCase().includes(searchLower))
    ).slice(0, 8);
  };

  // Funciones para autocompletado de recibo
  const handleReciboSearchChange = (e) => {
    const value = e.target.value;
    setRecibosBusqueda(value);
    setShowReciboSuggestions(value.length > 0 && !formData.pagoMultiple);
  };

  const handleReciboSelectFromSuggestions = (recibo) => {
    setRecibosBusqueda(recibo.numero_recibo);
    setFormData(prev => ({ ...prev, recibo: recibo.id.toString() }));
    setSelectedReceipt(recibo);
    setShowReciboSuggestions(false);
    
    if (formData.tipoPago === 'liquidar') {
      setFormData(prev => ({ ...prev, montoPago: recibo.saldo_pendiente.toString() }));
    }
  };

  const getFilteredRecibos = () => {
    if (!recibosBusqueda || formData.pagoMultiple) return [];
    
    const searchLower = recibosBusqueda.toLowerCase();
    return clientReceipts.filter(recibo => 
      recibo.numero_recibo.toLowerCase().includes(searchLower) ||
      (recibo.descripcion && recibo.descripcion.toLowerCase().includes(searchLower))
    ).slice(0, 8);
  };

  const calculateNewBalance = () => {
    if (!selectedReceipt) return 0;
    const payment = parseFloat(formData.montoPago || 0);
    return Math.max(0, selectedReceipt.saldo_pendiente - payment);
  };

  // Función para actualizar datos de pago de un recibo específico en modo múltiple
  const updateReciboPagoData = (reciboId, field, value) => {
    setRecibosConMonto(prevRecibos => 
      prevRecibos.map(r => 
        r.id === reciboId ? { ...r, [field]: value } : r
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar según el modo de pago
    if (formData.pagoMultiple) {
      // Validación para pago múltiple
      if (recibosConMonto.length === 0) {
        alert('Por favor seleccione al menos un recibo para pagar');
        return;
      }
      
      // Validar que todos los montos sean válidos
      const invalidMonto = recibosConMonto.find(r => !r.monto || parseFloat(r.monto) <= 0);
      if (invalidMonto) {
        alert('Por favor ingrese montos válidos para todos los recibos seleccionados');
        return;
      }
    } else {
      // Validación para pago único
      if (!formData.recibo || !formData.montoPago) {
        alert('Por favor complete todos los campos requeridos');
        return;
      }
    }
    
    // Preparar datos para confirmación y mostrar modal
    setPendingPaymentData({
      formData,
      selectedClient,
      selectedReceipt,
      recibosConMonto
    });
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = async () => {
    setShowConfirmModal(false);
    setSubmitting(true);
    
    try {
      if (pendingPaymentData.formData.pagoMultiple) {
        // Procesar pagos múltiples
        const resultados = [];
        const errores = [];
        
        for (const recibo of pendingPaymentData.recibosConMonto) {
          try {
            const pagoData = {
              recibo_id: parseInt(recibo.id),
              monto: parseFloat(recibo.monto),
              metodo_pago: recibo.metodoPago || 'transferencia',
              referencia: recibo.numeroReferencia || null,
              fecha_pago: formatDateForAPI(recibo.fecha || pendingPaymentData.formData.fecha),
              notas: recibo.notas || null
            };
            
            const response = await pagosAPI.create(pagoData);
            
            if (response.data.success) {
              resultados.push({
                numero_recibo: recibo.numero_recibo,
                monto: parseFloat(recibo.monto),
                id: response.data.data.id,
                metodoPago: recibo.metodoPago,
                fecha: recibo.fecha
              });
            }
          } catch (error) {
            console.error(`Error al registrar pago para recibo ${recibo.numero_recibo}:`, error);
            errores.push({
              numero_recibo: recibo.numero_recibo,
              error: error.message || 'Error desconocido'
            });
          }
        }
        
        // Mostrar resultados
        if (resultados.length > 0) {
          const totalPagado = resultados.reduce((sum, r) => sum + r.monto, 0);
          const payment = {
            ...pendingPaymentData.formData,
            client: pendingPaymentData.selectedClient,
            pagoMultiple: true,
            pagosRegistrados: resultados,
            totalPagado: totalPagado,
            errores: errores,
            timestamp: new Date().toLocaleString('es-MX')
          };
          
          setPaymentData(payment);
          setShowModal(true);
          
          if (errores.length > 0) {
            alert(`Se registraron ${resultados.length} pagos exitosamente, pero ${errores.length} fallaron. Ver detalles en el resumen.`);
          }
        } else {
          alert('No se pudo registrar ningún pago. Por favor intente nuevamente.');
        }
        
      } else {
        // Procesar pago único (código original)
        const pagoData = {
          recibo_id: parseInt(pendingPaymentData.formData.recibo),
          monto: parseFloat(pendingPaymentData.formData.montoPago),
          metodo_pago: pendingPaymentData.formData.metodoPago,
          referencia: pendingPaymentData.formData.numeroReferencia || null,
          fecha_pago: formatDateForAPI(pendingPaymentData.formData.fecha),
          notas: pendingPaymentData.formData.notas || null
        };
        
        const response = await pagosAPI.create(pagoData);
        
        if (response.data.success) {
          // Cargar el recibo completo con items si no están disponibles
          let reciboCompleto = pendingPaymentData.selectedReceipt;
          
          if (!reciboCompleto.items && !reciboCompleto.conceptos) {
            try {
              const reciboResponse = await recibosAPI.getById(pendingPaymentData.formData.recibo);
              reciboCompleto = reciboResponse.data.data;
            } catch (error) {
              console.error('Error al cargar items del recibo:', error);
            }
          }
          
          // Actualizar el estado del recibo después del pago
          const reciboActualizado = {
            ...reciboCompleto,
            estado: response.data.data.nuevo_saldo > 0.01 ? 'parcial' : 'pagado',
            saldo_pendiente: response.data.data.nuevo_saldo
          };
          
          const payment = {
            ...pendingPaymentData.formData,
            client: pendingPaymentData.selectedClient,
            receiptInfo: reciboActualizado,
            newBalance: response.data.data.nuevo_saldo,
            paymentAmount: parseFloat(pendingPaymentData.formData.montoPago),
            paymentNumber: `PAY-${response.data.data.id}`,
            timestamp: new Date().toLocaleString('es-MX')
          };
          
          setPaymentData(payment);
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert('Error al registrar pago: ' + (error.message || 'Error desconocido'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = async () => {
    try {
      const isPagoMultiple = paymentData?.pagoMultiple && paymentData?.pagosRegistrados;
      
      if (isPagoMultiple) {
        // Para pago múltiple, imprimir cada recibo
        for (let i = 0; i < paymentData.pagosRegistrados.length; i++) {
          const pago = paymentData.pagosRegistrados[i];
          
          let reciboCompleto = null;
          try {
            const response = await recibosAPI.getAll();
            const recibos = response.data.data || [];
            reciboCompleto = recibos.find(r => r.numero_recibo === pago.numero_recibo);
            
            if (!reciboCompleto) {
              console.error(`No se encontró el recibo ${pago.numero_recibo}`);
              continue;
            }
            
            const detalleResponse = await recibosAPI.getById(reciboCompleto.id);
            reciboCompleto = detalleResponse.data.data;
          } catch (error) {
            console.error(`Error al cargar recibo ${pago.numero_recibo}:`, error);
            continue;
          }
          
          const estadoActual = reciboCompleto.saldo_pendiente <= 0.01 ? 'pagado' : 'parcial';
          
          generarPDFIndividual({
            client: paymentData.client,
            receiptInfo: {
              ...reciboCompleto,
              estado: estadoActual
            },
            paymentAmount: parseFloat(pago.monto),
            metodoPago: pago.metodoPago || paymentData.metodoPago,
            numeroReferencia: pago.numeroReferencia || paymentData.numeroReferencia,
            timestamp: pago.fecha || paymentData.timestamp,
            newBalance: reciboCompleto.saldo_pendiente
          }, true); // true = imprimir en lugar de descargar
          
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } else {
        // Pago simple - imprimir un solo PDF
        generarPDFIndividual(paymentData, true);
      }
      
    } catch (error) {
      console.error('Error al imprimir PDF:', error);
      alert('Error al imprimir el PDF: ' + error.message);
    }
  };

  const handleDownload = async () => {
    try {
      const isPagoMultiple = paymentData?.pagoMultiple && paymentData?.pagosRegistrados;
      
      if (isPagoMultiple) {
        // PAGO MÚLTIPLE - Generar un PDF por cada recibo
        for (let i = 0; i < paymentData.pagosRegistrados.length; i++) {
          const pago = paymentData.pagosRegistrados[i];
          
          // Cargar datos completos del recibo
          let reciboCompleto = null;
          try {
            // Buscar el recibo por numero_recibo en lugar de id
            const response = await recibosAPI.getAll();
            const recibos = response.data.data || [];
            reciboCompleto = recibos.find(r => r.numero_recibo === pago.numero_recibo);
            
            if (!reciboCompleto) {
              console.error(`No se encontró el recibo ${pago.numero_recibo}`);
              continue;
            }
            
            // Cargar detalles completos del recibo
            const detalleResponse = await recibosAPI.getById(reciboCompleto.id);
            reciboCompleto = detalleResponse.data.data;
          } catch (error) {
            console.error(`Error al cargar recibo ${pago.numero_recibo}:`, error);
            continue; // Saltar este recibo si hay error
          }
          
          // Calcular estado después del pago
          const estadoActual = reciboCompleto.saldo_pendiente <= 0.01 ? 'pagado' : 'parcial';
          
          // Generar PDF individual para este recibo
          generarPDFIndividual({
            client: paymentData.client,
            receiptInfo: {
              ...reciboCompleto,
              estado: estadoActual
            },
            paymentAmount: parseFloat(pago.monto),
            metodoPago: pago.metodoPago || paymentData.metodoPago,
            numeroReferencia: pago.numeroReferencia || paymentData.numeroReferencia,
            timestamp: pago.fecha || paymentData.timestamp,
            newBalance: reciboCompleto.saldo_pendiente
          });
          
          // Pequeña pausa entre descargas para evitar problemas del navegador
          await new Promise(resolve => setTimeout(resolve, 800));
        }
        
        alert(`Se generaron ${paymentData.pagosRegistrados.length} comprobantes de pago.`);
      } else {
        // PAGO SIMPLE - Generar un solo PDF
        generarPDFIndividual(paymentData);
      }
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('Error al generar el PDF: ' + error.message);
    }
  };

  const generarPDFIndividual = (paymentInfo, print = false) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 12;

      // Configuración de la empresa
      const empresa = config?.nombre_empresa || 'Mi Empresa';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';
      const email = config?.email_contacto || '';
      const logoUrl = config?.logo_url || '';

      // Encabezado mejorado con logo y nombre de empresa
      const headerStartY = yPosition;
      
      // Agregar logo si existe (lado izquierdo)
      if (logoUrl && logoUrl.startsWith('data:image')) {
        try {
          const logoSize = 20;
          const logoX = 20;
          doc.addImage(logoUrl, 'PNG', logoX, yPosition, logoSize, logoSize);
        } catch (error) {
          console.error('Error al agregar logo:', error);
        }
      }

      // Información de la empresa (lado derecho del logo o centrado si no hay logo)
      const textX = logoUrl && logoUrl.startsWith('data:image') ? 45 : pageWidth / 2;
      const textAlign = logoUrl && logoUrl.startsWith('data:image') ? 'left' : 'center';
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(empresa, textX, yPosition + 5, { align: textAlign });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let infoY = yPosition + 10;
      
      if (direccion) {
        doc.text(direccion, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      if (telefono || email) {
        const contacto = [telefono, email].filter(Boolean).join(' | ');
        doc.text(contacto, textX, infoY, { align: textAlign });
        infoY += 4;
      }
      
      yPosition = Math.max(headerStartY + 20, infoY);

      // Línea separadora
      yPosition += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      yPosition += 6;

      // Título del documento
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('COMPROBANTE DE PAGO', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setTextColor(0, 0, 0);

      // Información del cliente - CONTENEDOR GRIS
      const containerMargin = 15;
      const containerWidth = pageWidth - (containerMargin * 2);
      
      doc.setFillColor(245, 245, 245);
      doc.rect(containerMargin, yPosition - 3, containerWidth, 28, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('CLIENTE', containerMargin + 5, yPosition);
      yPosition += 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const maxWidth = containerWidth - 10;
      const clientName = paymentInfo?.client?.nombre || paymentInfo?.cliente_nombre || 'N/A';
      
      // Nombre del cliente
      const clienteInfoLines = doc.splitTextToSize(clientName, maxWidth);
      doc.text(clienteInfoLines[0], containerMargin + 5, yPosition);
      yPosition += 5;
      
      // RFC y contacto en una línea
      let infoExtra = '';
      if (paymentInfo?.client?.rfc) {
        infoExtra = `RFC: ${paymentInfo.client.rfc}`;
      }
      if (paymentInfo?.client?.email || paymentInfo?.client?.telefono) {
        const contacto = [paymentInfo.client.email, paymentInfo.client.telefono].filter(Boolean).join(' | ');
        infoExtra += (infoExtra ? ' • ' : '') + contacto;
      }
      
      if (infoExtra) {
        doc.setFontSize(8);
        const infoLines = doc.splitTextToSize(infoExtra, maxWidth);
        doc.text(infoLines[0], containerMargin + 5, yPosition);
        yPosition += 4;
      }
      yPosition += 8;

      // Información del pago en dos columnas
      doc.setFontSize(9);
      const leftCol = containerMargin + 5;
      const rightCol = pageWidth / 2 + 10;
      const lineHeight = 5;

      doc.setFont('helvetica', 'bold');
      doc.text('Fecha de Pago:', leftCol, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(paymentInfo?.timestamp || new Date().toLocaleString('es-MX'), leftCol + 30, yPosition);

      doc.setFont('helvetica', 'bold');
      doc.text('Método de Pago:', rightCol, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(paymentInfo?.metodoPago || paymentInfo?.metodo_pago || 'N/A', rightCol + 35, yPosition);
      
      yPosition += lineHeight + 1;
      
      if (paymentInfo?.numeroReferencia || paymentInfo?.referencia) {
        doc.setFont('helvetica', 'bold');
        doc.text('Referencia:', leftCol, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(paymentInfo.numeroReferencia || paymentInfo.referencia || '', leftCol + 30, yPosition);
        yPosition += lineHeight + 1;
      }

      yPosition += 5;
      
      // Función para formatear moneda
      const formatCurrency = (value) => {
        return `$${parseFloat(value || 0).toFixed(2)}`;
      };

      // MOSTRAR DETALLES COMPLETOS DEL RECIBO
      const recibo = paymentInfo?.receiptInfo || {};
      
      // Encabezado de sección
      doc.setFillColor(240, 248, 255); // Azul muy claro
      doc.rect(containerMargin, yPosition - 2, containerWidth, 8, 'F');
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(91, 155, 213);
      doc.text('DETALLE DEL RECIBO', containerMargin + 5, yPosition + 3);
      doc.setTextColor(0, 0, 0);
      yPosition += 10;
      
      doc.setFontSize(9);
      
      // Información del recibo en dos columnas
      doc.setFont('helvetica', 'bold');
      doc.text('No. Recibo:', leftCol, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(recibo.numero_recibo || 'N/A', leftCol + 25, yPosition);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Fecha Emisión:', rightCol, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(recibo.fecha_emision ? new Date(recibo.fecha_emision).toLocaleDateString('es-MX') : 'N/A', rightCol + 30, yPosition);
      yPosition += 5;
      
      // Determinar estado correcto del recibo después del pago
      const montoPagado = parseFloat(paymentInfo.paymentAmount || paymentInfo.montoPago || 0);
      const totalRecibo = parseFloat(recibo.monto_total || recibo.total || 0);
      const saldoReal = paymentInfo.newBalance !== undefined 
        ? parseFloat(paymentInfo.newBalance) 
        : Math.max(0, totalRecibo - montoPagado);
      
      let estadoActual = recibo.estado || 'pendiente';
      if (saldoReal <= 0.01) {
        estadoActual = 'pagado';
      } else if (saldoReal < totalRecibo) {
        estadoActual = 'parcial';
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text('Estado:', leftCol, yPosition);
      doc.setFont('helvetica', 'normal');
      const estadoColor = estadoActual === 'pagado' ? [0, 128, 0] : estadoActual === 'pendiente' ? [255, 140, 0] : [0, 100, 200];
      doc.setTextColor(...estadoColor);
      doc.text(estadoActual.toUpperCase(), leftCol + 25, yPosition);
      doc.setTextColor(0, 0, 0);
      
      if (recibo.fecha_vencimiento) {
        doc.setFont('helvetica', 'bold');
        doc.text('Vencimiento:', rightCol, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX'), rightCol + 30, yPosition);
      }
      yPosition += 5;
      
      yPosition += 3;
      
      // Tabla de conceptos/items
      const items = recibo.items || recibo.conceptos || [];
      
      if (items && items.length > 0) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('CONCEPTOS:', containerMargin + 5, yPosition);
        yPosition += 5;
        
        const tableData = items.map(item => [
          item.descripcion || 'N/A',
          item.cantidad?.toString() || '1',
          formatCurrency(item.precio_unitario || 0),
          formatCurrency((item.cantidad || 1) * (item.precio_unitario || 0))
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          head: [['Concepto', 'Cant.', 'P. Unitario', 'Total']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: [91, 155, 213],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 9,
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 100, halign: 'left' },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 30, halign: 'right' },
            3: { cellWidth: 30, halign: 'right' }
          },
          styles: {
            fontSize: 9,
            cellPadding: 3
          },
          margin: { left: 15, right: 15 }
        });

        yPosition = doc.lastAutoTable.finalY + 8;
      } else {
        yPosition += 2;
      }
      
      // Totales del recibo
      const rightAlign = pageWidth - 20;
      const labelX = rightAlign - 55;
      const valueX = rightAlign - 5;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      // Subtotal
      if (recibo.subtotal) {
        doc.text('Subtotal:', labelX, yPosition, { align: 'right' });
        doc.text(formatCurrency(recibo.subtotal), valueX, yPosition, { align: 'right' });
        yPosition += 5;
      }
      
      // Descuento
      if (recibo.monto_descuento && recibo.monto_descuento > 0) {
        doc.text(`Descuento (${recibo.descuento || 0}%):`, labelX, yPosition, { align: 'right' });
        doc.text(`-${formatCurrency(recibo.monto_descuento)}`, valueX, yPosition, { align: 'right' });
        yPosition += 5;
      }
      
      // IVA
      if (recibo.monto_iva && recibo.monto_iva > 0) {
        doc.text(`IVA (${recibo.iva || 16}%):`, labelX, yPosition, { align: 'right' });
        doc.text(formatCurrency(recibo.monto_iva), valueX, yPosition, { align: 'right' });
        yPosition += 5;
      }
      
      // Línea separadora
      doc.setLineWidth(0.5);
      doc.setDrawColor(200, 200, 200);
      doc.line(labelX - 10, yPosition, rightAlign, yPosition);
      yPosition += 6;
      
      // Total del recibo
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Total Recibo:', labelX, yPosition, { align: 'right' });
      doc.text(formatCurrency(recibo.monto_total || recibo.total || 0), valueX, yPosition, { align: 'right' });
      yPosition += 2;
      
      // Línea separadora
      doc.setLineWidth(0.3);
      doc.line(labelX - 10, yPosition, rightAlign, yPosition);
      yPosition += 5;
      
      // Monto pagado
      doc.setFontSize(11);
      doc.setTextColor(0, 128, 0); // Verde
      doc.text('MONTO PAGADO:', labelX, yPosition, { align: 'right' });
      doc.text(formatCurrency(montoPagado), valueX, yPosition, { align: 'right' });
      doc.setTextColor(0, 0, 0); // Volver a negro
      yPosition += 2;
      
      // Línea separadora
      doc.setLineWidth(0.3);
      doc.line(labelX - 10, yPosition, rightAlign, yPosition);
      yPosition += 5;
      
      // Calcular saldo pendiente correcto
      let saldoPendiente = 0;
      
      if (paymentInfo.newBalance !== undefined) {
        saldoPendiente = parseFloat(paymentInfo.newBalance);
      } else if (recibo.saldo_pendiente !== undefined) {
        saldoPendiente = parseFloat(recibo.saldo_pendiente);
      } else {
        saldoPendiente = Math.max(0, totalRecibo - montoPagado);
      }
      
      // Mostrar saldo pendiente
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      if (saldoPendiente > 0.01) {
        doc.setTextColor(255, 140, 0); // Naranja
        doc.text('Saldo Pendiente:', labelX, yPosition, { align: 'right' });
        doc.text(formatCurrency(saldoPendiente), valueX, yPosition, { align: 'right' });
      } else {
        doc.setTextColor(0, 128, 0); // Verde
        doc.text('PAGADO COMPLETO', labelX, yPosition, { align: 'right' });
        doc.text('$0.00', valueX, yPosition, { align: 'right' });
      }
      doc.setTextColor(0, 0, 0);

      // Pie de página
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Gracias por su pago', pageWidth / 2, pageHeight - 20, { align: 'center' });
      doc.text(`Documento generado el ${new Date().toLocaleString('es-MX')}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

      // Imprimir o Descargar PDF
      const clientNameClean = clientName.replace(/\s/g, '_');
      const reciboNum = recibo.numero_recibo || 'Recibo';
      
      if (print) {
        // Abrir en nueva ventana para imprimir
        window.open(doc.output('bloburl'), '_blank');
      } else {
        // Descargar PDF
        doc.save(`Comprobante_${reciboNum}_${clientNameClean}.pdf`);
      }
      
    } catch (error) {
      console.error('Error al generar PDF:', error);
      throw error;
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate('/recibos');
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const canSubmit = formData.cliente && (
    formData.pagoMultiple 
      ? recibosConMonto.length > 0 && recibosConMonto.every(r => r.monto && parseFloat(r.monto) > 0)
      : formData.recibo && formData.montoPago && parseFloat(formData.montoPago) > 0
  );

  return (
    <RegisterPaymentContainer>
      <PageHeader>
        <BackButton onClick={handleCancel}>
          <MdArrowBack />
        </BackButton>
        <Title>Registrar Pago</Title>
      </PageHeader>

      <FormCard>
        <form onSubmit={handleSubmit}>
          {/* Step Indicator */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: '100px',
            marginBottom: '40px',
            position: 'relative'
          }}>
            <div style={{ 
              position: 'absolute', 
              top: '20px', 
              left: 'calc(50% - 150px)', 
              right: 'calc(50% - 150px)', 
              height: '3px', 
              backgroundColor: formData.cliente && (formData.recibo || selectedRecibos.length > 0) ? '#6366f1' : '#e5e7eb',
              zIndex: 0
            }} />
            <StepItem>
              <StepNumber active={true}>1</StepNumber>
              <StepLabel>Cliente</StepLabel>
            </StepItem>
            <StepItem>
              <StepNumber active={formData.cliente}>2</StepNumber>
              <StepLabel>Recibos</StepLabel>
            </StepItem>
            <StepItem>
              <StepNumber active={formData.cliente && (formData.recibo || selectedRecibos.length > 0)}>3</StepNumber>
              <StepLabel>Pago</StepLabel>
            </StepItem>
          </div>

          <FormSection>
            <SectionTitle>
              <MdPerson />
              Paso 1: Selecciona el Cliente
            </SectionTitle>
            <SectionDescription>
              Busca y selecciona el cliente que realizará el pago
            </SectionDescription>

            <FormGroup>
              {(!formData.cliente && !selectedClient) ? (
                <>
                  <SearchBox>
                    <AutocompleteContainer data-autocomplete="true">
                      <Input
                        type="text"
                        placeholder="🔍 Buscar cliente por nombre, RFC o email..."
                        value={clientesBusqueda}
                        onChange={handleClienteSearchChange}
                        onFocus={() => setShowClienteSuggestions(true)}
                        disabled={loading}
                        autoComplete="off"
                      />
                      {showClienteSuggestions && clientesBusqueda.trim().length >= 2 && (
                        <SuggestionsDropdown>
                          {getFilteredClientes().length > 0 ? (
                            getFilteredClientes().map(cliente => (
                              <SuggestionItem
                                key={cliente.id}
                                selected={formData.cliente === cliente.id.toString()}
                                onClick={() => handleClienteSelectFromSuggestions(cliente)}
                              >
                                <SuggestionName>{cliente.nombre}</SuggestionName>
                                <SuggestionDetails>
                                  {cliente.rfc && <span>RFC: {cliente.rfc}</span>}
                                  {cliente.email && <span>Email: {cliente.email}</span>}
                                  {cliente.saldo_pendiente > 0 && (
                                    <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                      💰 ${parseFloat(cliente.saldo_pendiente).toFixed(2)}
                                    </span>
                                  )}
                                </SuggestionDetails>
                              </SuggestionItem>
                            ))
                          ) : (
                            <NoResults>No se encontraron clientes con "{clientesBusqueda}"</NoResults>
                          )}
                        </SuggestionsDropdown>
                      )}
                    </AutocompleteContainer>
                  </SearchBox>
                  
                  {!clientesBusqueda && (
                    <div style={{ 
                      border: '2px dashed #e5e7eb',
                      borderRadius: '12px',
                      padding: '60px 20px',
                      backgroundColor: '#fafafa',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Busca un cliente para continuar
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Escribe al menos 2 caracteres del nombre, RFC o email del cliente
                      </div>
                    </div>
                  )}
                </>
              ) : (
                (formData.cliente || selectedClient) && (
                  <div style={{ 
                    border: '2px solid #6366f1',
                    borderRadius: '12px',
                    padding: '20px',
                    backgroundColor: '#f0f9ff',
                    marginTop: '0px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                          ✅ Cliente seleccionado
                        </div>
                        {selectedClient ? (
                          <>
                            <div style={{ fontSize: '16px', color: '#374151', marginBottom: '4px' }}>
                              {selectedClient.nombre}
                            </div>
                            {selectedClient.email && (
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                📧 {selectedClient.email}
                              </div>
                            )}
                            {selectedClient.rfc && (
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                🏢 RFC: {selectedClient.rfc}
                              </div>
                            )}
                            {selectedClient.saldo_pendiente > 0 && (
                              <div style={{ fontSize: '14px', color: '#ef4444', fontWeight: '600', marginTop: '8px' }}>
                                💰 Saldo pendiente: ${parseFloat(selectedClient.saldo_pendiente).toFixed(2)}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ fontSize: '14px', color: '#6b7280' }}>
                            Cargando información del cliente...
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setFormData(prev => ({ ...prev, cliente: '', recibo: '' }));
                          setSelectedClient(null);
                          setClientesBusqueda('');
                          setSelectedRecibos([]);
                          setRecibosConMonto([]);
                        }}
                        style={{
                          padding: '10px 20px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease',
                          whiteSpace: 'nowrap',
                          marginLeft: '20px'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#dc2626'}
                        onMouseOut={(e) => e.target.style.background = '#ef4444'}
                      >
                        Cambiar cliente
                      </button>
                    </div>
                  </div>
                )
              )}
            </FormGroup>
          </FormSection>

          {formData.cliente && (
            <FormSection>
              <SectionTitle>
                <MdReceipt />
                Paso 2: Selecciona los Recibos
              </SectionTitle>
              <SectionDescription>
                Elige uno o varios recibos para registrar el pago
              </SectionDescription>

              <FormGroup style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  padding: '12px 16px',
                  background: '#f0f9ff',
                  border: '2px solid #bae6fd',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    name="pagoMultiple"
                    checked={formData.pagoMultiple}
                    onChange={(e) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        pagoMultiple: e.target.checked, 
                        recibo: '',
                        montoPago: '',
                        tipoPago: 'liquidar'
                      }));
                      if (e.target.checked) {
                        // Limpiar selección de pago único
                        setSelectedReceipt(null);
                        setSelectedRecibos([]);
                        setRecibosConMonto([]);
                      }
                    }}
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span style={{ fontWeight: '600', color: '#0c4a6e' }}>
                    ✨ Pagar múltiples recibos a la vez
                  </span>
                </label>
              </FormGroup>

              <FormGroup>
                <SearchBox>
                  <AutocompleteContainer data-autocomplete="true">
                    <Input
                      type="text"
                      placeholder="🔍 Buscar recibo por número o descripción..."
                      value={recibosBusqueda}
                      onChange={handleReciboSearchChange}
                      onFocus={() => recibosBusqueda && !formData.pagoMultiple && setShowReciboSuggestions(true)}
                    />
                    {showReciboSuggestions && !formData.pagoMultiple && getFilteredRecibos().length > 0 && (
                      <SuggestionsDropdown>
                        {getFilteredRecibos().map(recibo => (
                          <SuggestionItem
                            key={recibo.id}
                            selected={formData.recibo === recibo.id.toString()}
                            onClick={() => handleReciboSelectFromSuggestions(recibo)}
                          >
                            <SuggestionName>Recibo #{recibo.numero_recibo}</SuggestionName>
                            <SuggestionDetails>
                              {recibo.descripcion && <span>{recibo.descripcion}</span>}
                              <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                Saldo: ${parseFloat(recibo.saldo_pendiente).toFixed(2)}
                              </span>
                              <span style={{ color: '#6b7280' }}>
                                Vence: {new Date(recibo.fecha_vencimiento).toLocaleDateString('es-MX')}
                              </span>
                            </SuggestionDetails>
                          </SuggestionItem>
                        ))}
                      </SuggestionsDropdown>
                    )}
                    {showReciboSuggestions && !formData.pagoMultiple && recibosBusqueda && getFilteredRecibos().length === 0 && (
                      <SuggestionsDropdown>
                        <NoResults>No se encontraron recibos</NoResults>
                      </SuggestionsDropdown>
                    )}
                  </AutocompleteContainer>
                </SearchBox>

                {clientReceipts.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    background: '#fef3c7',
                    border: '2px dashed #fbbf24',
                    borderRadius: '12px',
                    color: '#92400e'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📋</div>
                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>No hay recibos pendientes</div>
                    <div style={{ fontSize: '14px' }}>Este cliente no tiene recibos con saldo pendiente</div>
                  </div>
                ) : (
                  <div style={{ 
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '10px',
                    backgroundColor: '#f9fafb'
                  }}>
                    {!formData.pagoMultiple ? (
                      // Modo de pago único
                      clientReceipts
                        .filter(recibo => 
                          recibo.numero_recibo.toLowerCase().includes(recibosBusqueda.toLowerCase()) ||
                          recibo.descripcion.toLowerCase().includes(recibosBusqueda.toLowerCase())
                        )
                        .map(recibo => (
                          <ReciboCard
                            key={recibo.id}
                            selected={formData.recibo === recibo.id.toString()}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, recibo: recibo.id.toString() }));
                              setSelectedReceipt(recibo);
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                              <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e40af' }}>
                                {recibo.numero_recibo}
                              </div>
                              <div style={{ 
                                background: '#fef2f2', 
                                color: '#dc2626', 
                                padding: '4px 12px', 
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: '700'
                              }}>
                                ${parseFloat(recibo.saldo_pendiente || 0).toFixed(2)}
                              </div>
                            </div>
                            <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: '6px' }}>
                              {recibo.descripcion}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              Total: ${parseFloat(recibo.monto_total || 0).toFixed(2)} | 
                              Estado: <span style={{ 
                                color: recibo.estado === 'parcial' ? '#f59e0b' : '#10b981',
                                fontWeight: '600'
                              }}>{recibo.estado}</span>
                            </div>
                          </ReciboCard>
                        ))
                    ) : (
                      // Modo de pago múltiple
                      clientReceipts
                        .filter(recibo => 
                          recibo.numero_recibo.toLowerCase().includes(recibosBusqueda.toLowerCase()) ||
                          recibo.descripcion.toLowerCase().includes(recibosBusqueda.toLowerCase())
                        )
                        .map(recibo => (
                          <ReciboCard
                            key={recibo.id}
                            checkbox
                            selected={selectedRecibos.includes(recibo.id)}
                          >
                            <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                              <input
                                type="checkbox"
                                checked={selectedRecibos.includes(recibo.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedRecibos([...selectedRecibos, recibo.id]);
                                    setRecibosConMonto([...recibosConMonto, { 
                                      id: recibo.id, 
                                      monto: recibo.saldo_pendiente, // ✅ Auto-selecciona el monto completo
                                      numero_recibo: recibo.numero_recibo,
                                      saldo_maximo: recibo.saldo_pendiente,
                                      descripcion: recibo.descripcion,
                                      monto_total: recibo.monto_total,
                                      estado: recibo.estado,
                                      // Datos de pago individuales para cada recibo
                                      fecha: today,
                                      metodoPago: 'Transferencia Bancaria',
                                      numeroReferencia: '',
                                      notas: '',
                                      tipoPago: 'liquidar' // ✅ Auto-selecciona "liquidar"
                                    }]);
                                  } else {
                                    setSelectedRecibos(selectedRecibos.filter(id => id !== recibo.id));
                                    setRecibosConMonto(recibosConMonto.filter(r => r.id !== recibo.id));
                                  }
                                }}
                                style={{ width: '22px', height: '22px', cursor: 'pointer', marginTop: '2px' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                                  <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e40af' }}>
                                    {recibo.numero_recibo}
                                  </div>
                                  <div style={{ 
                                    background: '#fef2f2', 
                                    color: '#dc2626', 
                                    padding: '4px 12px', 
                                    borderRadius: '20px',
                                    fontSize: '14px',
                                    fontWeight: '700'
                                  }}>
                                    ${parseFloat(recibo.saldo_pendiente || 0).toFixed(2)}
                                  </div>
                                </div>
                                <div style={{ fontSize: '14px', color: '#4b5563', marginBottom: '10px' }}>
                                  {recibo.descripcion}
                                </div>
                                
                                {selectedRecibos.includes(recibo.id) && (() => {
                                  const reciboData = recibosConMonto.find(r => r.id === recibo.id);
                                  return (
                                    <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
                                      {/* Información del Recibo */}
                                      <ReceiptInfoCard>
                                        <ReceiptInfoTitle>Información del Recibo</ReceiptInfoTitle>
                                        <ReceiptInfoRow>
                                          <ReceiptInfoLabel>Número de Recibo:</ReceiptInfoLabel>
                                          <ReceiptInfoValue>{reciboData?.numero_recibo}</ReceiptInfoValue>
                                        </ReceiptInfoRow>
                                        <ReceiptInfoRow>
                                          <ReceiptInfoLabel>Descripción:</ReceiptInfoLabel>
                                          <ReceiptInfoValue>{reciboData?.descripcion}</ReceiptInfoValue>
                                        </ReceiptInfoRow>
                                        <ReceiptInfoRow>
                                          <ReceiptInfoLabel>Monto Total:</ReceiptInfoLabel>
                                          <ReceiptInfoValue>${parseFloat(reciboData?.monto_total || 0).toFixed(2)}</ReceiptInfoValue>
                                        </ReceiptInfoRow>
                                        <ReceiptInfoRow>
                                          <ReceiptInfoLabel>Saldo Pendiente:</ReceiptInfoLabel>
                                          <ReceiptInfoValue style={{ color: '#E57373' }}>
                                            ${parseFloat(reciboData?.saldo_maximo || 0).toFixed(2)}
                                          </ReceiptInfoValue>
                                        </ReceiptInfoRow>
                                      </ReceiptInfoCard>

                                      {/* Tipo de Pago */}
                                      <FormSection style={{ marginTop: '20px' }}>
                                        <SectionTitle>
                                          <MdEuro />
                                          Tipo de Pago
                                        </SectionTitle>
                                        <SectionDescription>
                                          Selecciona si deseas abonar una cantidad o liquidar el recibo completamente
                                        </SectionDescription>
                                        
                                        <PaymentTypeButtons>
                                          <PaymentTypeButton
                                            type="button"
                                            active={reciboData?.tipoPago === 'abonar'}
                                            onClick={() => {
                                              updateReciboPagoData(recibo.id, 'tipoPago', 'abonar');
                                              updateReciboPagoData(recibo.id, 'monto', '');
                                            }}
                                          >
                                            <PaymentTypeTitle>💰 Abonar</PaymentTypeTitle>
                                            <PaymentTypeDescription>Pagar una cantidad parcial</PaymentTypeDescription>
                                          </PaymentTypeButton>

                                          <PaymentTypeButton
                                            type="button"
                                            active={reciboData?.tipoPago === 'liquidar'}
                                            onClick={() => {
                                              updateReciboPagoData(recibo.id, 'tipoPago', 'liquidar');
                                              updateReciboPagoData(recibo.id, 'monto', recibo.saldo_pendiente);
                                            }}
                                          >
                                            <PaymentTypeTitle>✅ Liquidar</PaymentTypeTitle>
                                            <PaymentTypeDescription>Pagar el saldo completo</PaymentTypeDescription>
                                          </PaymentTypeButton>
                                        </PaymentTypeButtons>

                                        <FormGrid>
                                          <FormGroup>
                                            <Label>
                                              Monto a Pagar <span className="required">*</span>
                                            </Label>
                                            <Input
                                              type="number"
                                              value={reciboData?.monto || ''}
                                              onChange={(e) => updateReciboPagoData(recibo.id, 'monto', e.target.value)}
                                              placeholder="0.00"
                                              step="0.01"
                                              min="0.01"
                                              max={recibo.saldo_pendiente}
                                              disabled={reciboData?.tipoPago === 'liquidar'}
                                              required
                                            />
                                          </FormGroup>

                                          <FormGroup>
                                            <Label>
                                              <MdCalendarToday style={{ fontSize: '1rem' }} />
                                              Fecha de Pago <span className="required">*</span>
                                            </Label>
                                            <Input
                                              type="date"
                                              value={reciboData?.fecha || today}
                                              onChange={(e) => updateReciboPagoData(recibo.id, 'fecha', e.target.value)}
                                              required
                                            />
                                          </FormGroup>
                                        </FormGrid>
                                      </FormSection>

                                      {/* Método de Pago */}
                                      <FormSection style={{ marginTop: '20px' }}>
                                        <SectionTitle>
                                          <MdPayment />
                                          Método de Pago
                                        </SectionTitle>
                                        
                                        <FormGrid>
                                          <FormGroup>
                                            <Label>Método de Pago <span className="required">*</span></Label>
                                            <Select
                                              value={reciboData?.metodoPago || 'Transferencia Bancaria'}
                                              onChange={(e) => updateReciboPagoData(recibo.id, 'metodoPago', e.target.value)}
                                              required
                                            >
                                              <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                                              <option value="Tarjeta de Crédito">Tarjeta de Crédito</option>
                                              <option value="Tarjeta de Débito">Tarjeta de Débito</option>
                                              <option value="PayPal">PayPal</option>
                                              <option value="Efectivo">Efectivo</option>
                                              <option value="Cheque">Cheque</option>
                                            </Select>
                                          </FormGroup>

                                          <FormGroup>
                                            <Label>Número de Referencia</Label>
                                            <Input
                                              type="text"
                                              value={reciboData?.numeroReferencia || ''}
                                              onChange={(e) => updateReciboPagoData(recibo.id, 'numeroReferencia', e.target.value)}
                                              placeholder="Ej: TRF-2024-001234"
                                            />
                                          </FormGroup>
                                        </FormGrid>

                                        <FormGroup style={{ marginTop: '16px' }}>
                                          <Label>Notas</Label>
                                          <textarea
                                            value={reciboData?.notas || ''}
                                            onChange={(e) => updateReciboPagoData(recibo.id, 'notas', e.target.value)}
                                            placeholder="Observaciones adicionales sobre este pago..."
                                            rows="3"
                                            style={{
                                              width: '100%',
                                              padding: '12px',
                                              border: '1px solid #e5e7eb',
                                              borderRadius: '8px',
                                              fontSize: '14px',
                                              fontFamily: 'inherit',
                                              resize: 'vertical'
                                            }}
                                          />
                                        </FormGroup>
                                      </FormSection>

                                      {/* Resumen Individual */}
                                      {reciboData?.monto && parseFloat(reciboData.monto) > 0 && (
                                        <SummaryCard style={{ marginTop: '20px' }}>
                                          <SummaryTitle>Resumen del Pago</SummaryTitle>
                                          <SummaryRow>
                                            <SummaryLabel>Saldo Actual:</SummaryLabel>
                                            <SummaryValue>${parseFloat(recibo.saldo_pendiente || 0).toFixed(2)}</SummaryValue>
                                          </SummaryRow>
                                          <SummaryRow>
                                            <SummaryLabel>Monto a Pagar:</SummaryLabel>
                                            <SummaryValue>${parseFloat(reciboData.monto || 0).toFixed(2)}</SummaryValue>
                                          </SummaryRow>
                                          <SummaryRow>
                                            <SummaryLabel>Nuevo Saldo:</SummaryLabel>
                                            <SummaryValue large>
                                              ${Math.max(0, parseFloat(recibo.saldo_pendiente) - parseFloat(reciboData.monto || 0)).toFixed(2)}
                                            </SummaryValue>
                                          </SummaryRow>
                                        </SummaryCard>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </ReciboCard>
                        ))
                    )}
                  </div>
                )}
              </FormGroup>
            </FormSection>
          )}

          {selectedReceipt && !formData.pagoMultiple && (
            <>
              <ReceiptInfoCard>
                <ReceiptInfoTitle>Información del Recibo</ReceiptInfoTitle>
                <ReceiptInfoRow>
                  <ReceiptInfoLabel>Número de Recibo:</ReceiptInfoLabel>
                  <ReceiptInfoValue>{selectedReceipt.numero_recibo}</ReceiptInfoValue>
                </ReceiptInfoRow>
                <ReceiptInfoRow>
                  <ReceiptInfoLabel>Descripción:</ReceiptInfoLabel>
                  <ReceiptInfoValue>{selectedReceipt.descripcion}</ReceiptInfoValue>
                </ReceiptInfoRow>
                <ReceiptInfoRow>
                  <ReceiptInfoLabel>Monto Total:</ReceiptInfoLabel>
                  <ReceiptInfoValue>${parseFloat(selectedReceipt.monto_total || 0).toFixed(2)}</ReceiptInfoValue>
                </ReceiptInfoRow>
                <ReceiptInfoRow>
                  <ReceiptInfoLabel>Saldo Pendiente:</ReceiptInfoLabel>
                  <ReceiptInfoValue style={{ color: parseFloat(selectedReceipt.saldo_pendiente || 0) > 0 ? '#E57373' : '#4CAF50' }}>
                    ${parseFloat(selectedReceipt.saldo_pendiente || 0).toFixed(2)}
                  </ReceiptInfoValue>
                </ReceiptInfoRow>
              </ReceiptInfoCard>

              <FormSection>
                <SectionTitle>
                  <MdEuro />
                  Tipo de Pago
                </SectionTitle>
                <SectionDescription>
                  Selecciona si deseas abonar una cantidad o liquidar el recibo completamente
                </SectionDescription>
                
                <PaymentTypeButtons>
                  <PaymentTypeButton
                    type="button"
                    active={formData.tipoPago === 'abonar'}
                    onClick={() => handlePaymentTypeChange('abonar')}
                  >
                    <PaymentTypeTitle>💰 Abonar</PaymentTypeTitle>
                    <PaymentTypeDescription>Pagar una cantidad parcial</PaymentTypeDescription>
                  </PaymentTypeButton>

                  <PaymentTypeButton
                    type="button"
                    active={formData.tipoPago === 'liquidar'}
                    onClick={() => handlePaymentTypeChange('liquidar')}
                    disabled={parseFloat(selectedReceipt.saldo_pendiente || 0) === 0}
                  >
                    <PaymentTypeTitle>✅ Liquidar</PaymentTypeTitle>
                    <PaymentTypeDescription>Pagar el saldo completo</PaymentTypeDescription>
                  </PaymentTypeButton>
                </PaymentTypeButtons>

                <FormGrid>
                  <FormGroup>
                    <Label>
                      Monto a Pagar <span className="required">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="montoPago"
                      value={formData.montoPago}
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0.01"
                      max={selectedReceipt.saldo_pendiente}
                      disabled={formData.tipoPago === 'liquidar'}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      <MdCalendarToday style={{ fontSize: '1rem' }} />
                      Fecha de Pago <span className="required">*</span>
                    </Label>
                    <Input
                      type="date"
                      name="fecha"
                      value={formData.fecha}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>
                  <MdPayment />
                  Método de Pago
                </SectionTitle>
                
                <FormGrid>
                  <FormGroup>
                    <Label>Método de Pago <span className="required">*</span></Label>
                    <Select
                      name="metodoPago"
                      value={formData.metodoPago}
                      onChange={handleChange}
                      required
                    >
                      <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                      <option value="Tarjeta de Crédito">Tarjeta de Crédito</option>
                      <option value="Tarjeta de Débito">Tarjeta de Débito</option>
                      <option value="PayPal">PayPal</option>
                      <option value="Efectivo">Efectivo</option>
                      <option value="Cheque">Cheque</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Número de Referencia</Label>
                    <Input
                      type="text"
                      name="numeroReferencia"
                      value={formData.numeroReferencia}
                      onChange={handleChange}
                      placeholder="Ej: TRF-2024-001234"
                    />
                  </FormGroup>
                </FormGrid>
              </FormSection>

              {formData.montoPago && parseFloat(formData.montoPago) > 0 && (
                <SummaryCard>
                  <SummaryTitle>Resumen del Pago</SummaryTitle>
                  <SummaryRow>
                    <SummaryLabel>Saldo Actual:</SummaryLabel>
                    <SummaryValue>${parseFloat(selectedReceipt.saldo_pendiente || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>Monto a Pagar:</SummaryLabel>
                    <SummaryValue>${parseFloat(formData.montoPago || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                  <SummaryRow>
                    <SummaryLabel>Nuevo Saldo:</SummaryLabel>
                    <SummaryValue large>${parseFloat(calculateNewBalance() || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                </SummaryCard>
              )}
            </>
          )}

          {/* Resumen de Pago Múltiple - fuera del bloque de selectedReceipt */}
          {formData.pagoMultiple && recibosConMonto.length > 0 && (
            <SummaryCard>
              <SummaryTitle>Resumen del Pago Múltiple</SummaryTitle>
              {recibosConMonto.map((recibo, index) => (
                <div key={recibo.id} style={{ 
                  marginBottom: index < recibosConMonto.length - 1 ? '12px' : '0',
                  paddingBottom: index < recibosConMonto.length - 1 ? '12px' : '0',
                  borderBottom: index < recibosConMonto.length - 1 ? '1px solid #e5e7eb' : 'none'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '6px', color: '#6366f1' }}>
                    {recibo.numero_recibo}
                  </div>
                  <SummaryRow>
                    <SummaryLabel>Monto a pagar:</SummaryLabel>
                    <SummaryValue>${parseFloat(recibo.monto || 0).toFixed(2)}</SummaryValue>
                  </SummaryRow>
                </div>
              ))}
              <SummaryRow style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #6366f1' }}>
                <SummaryLabel style={{ fontWeight: '700' }}>Total a Pagar:</SummaryLabel>
                <SummaryValue large style={{ color: '#6366f1' }}>
                  ${recibosConMonto.reduce((sum, r) => sum + parseFloat(r.monto || 0), 0).toFixed(2)}
                </SummaryValue>
              </SummaryRow>
            </SummaryCard>
          )}

          <ButtonGroup>
            <Button type="button" onClick={handleCancel} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" primary disabled={!canSubmit || submitting}>
              <MdSave />
              {submitting ? 'Procesando...' : 'Registrar Pago'}
            </Button>
          </ButtonGroup>
        </form>
      </FormCard>

      {/* Confirmation Modal */}
      {showConfirmModal && pendingPaymentData && (
        <ModalOverlay onClick={() => setShowConfirmModal(false)}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ConfirmModalTitle>
                <MdPayment />
                Confirmar Pago
              </ConfirmModalTitle>
              <CloseButton onClick={() => setShowConfirmModal(false)}>
                <MdClose />
              </CloseButton>
            </ModalHeader>

            <ConfirmModalBody>
              <ConfirmMessage>
                ¿Está seguro de que desea registrar este pago?
              </ConfirmMessage>

              <ConfirmDetails>
                {pendingPaymentData.formData.pagoMultiple ? (
                  <>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Cliente:</ConfirmDetailLabel>
                      <ConfirmDetailValue>{pendingPaymentData.selectedClient?.nombre}</ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Recibos a pagar:</ConfirmDetailLabel>
                      <ConfirmDetailValue>{pendingPaymentData.recibosConMonto.length}</ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Fecha de pago:</ConfirmDetailLabel>
                      <ConfirmDetailValue>
                        {new Date(pendingPaymentData.formData.fecha).toLocaleDateString('es-MX')}
                      </ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Total a pagar:</ConfirmDetailLabel>
                      <ConfirmDetailValue style={{ color: '#4CAF50', fontSize: '1.5rem' }}>
                        ${pendingPaymentData.recibosConMonto.reduce((sum, r) => sum + parseFloat(r.monto || 0), 0).toFixed(2)}
                      </ConfirmDetailValue>
                    </ConfirmDetailRow>
                  </>
                ) : (
                  <>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Cliente:</ConfirmDetailLabel>
                      <ConfirmDetailValue>{pendingPaymentData.selectedClient?.nombre}</ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Recibo:</ConfirmDetailLabel>
                      <ConfirmDetailValue>{pendingPaymentData.selectedReceipt?.numero_recibo}</ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Método de pago:</ConfirmDetailLabel>
                      <ConfirmDetailValue style={{ textTransform: 'capitalize' }}>
                        {pendingPaymentData.formData.metodoPago}
                      </ConfirmDetailValue>
                    </ConfirmDetailRow>
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Fecha de pago:</ConfirmDetailLabel>
                      <ConfirmDetailValue>
                        {new Date(pendingPaymentData.formData.fecha).toLocaleDateString('es-MX')}
                      </ConfirmDetailValue>
                    </ConfirmDetailRow>
                    {pendingPaymentData.formData.numeroReferencia && (
                      <ConfirmDetailRow>
                        <ConfirmDetailLabel>Referencia:</ConfirmDetailLabel>
                        <ConfirmDetailValue>{pendingPaymentData.formData.numeroReferencia}</ConfirmDetailValue>
                      </ConfirmDetailRow>
                    )}
                    <ConfirmDetailRow>
                      <ConfirmDetailLabel>Monto a pagar:</ConfirmDetailLabel>
                      <ConfirmDetailValue style={{ color: '#4CAF50', fontSize: '1.5rem' }}>
                        ${parseFloat(pendingPaymentData.formData.montoPago || 0).toFixed(2)}
                      </ConfirmDetailValue>
                    </ConfirmDetailRow>
                  </>
                )}
              </ConfirmDetails>

              <ConfirmButtons>
                <ConfirmButtonSecondary 
                  type="button" 
                  onClick={() => setShowConfirmModal(false)}
                  disabled={submitting}
                >
                  <MdClose />
                  Cancelar
                </ConfirmButtonSecondary>
                <ConfirmButtonPrimary 
                  type="button" 
                  onClick={handleConfirmPayment}
                  disabled={submitting}
                >
                  <MdCheckCircle />
                  {submitting ? 'Procesando...' : 'Confirmar Pago'}
                </ConfirmButtonPrimary>
              </ConfirmButtons>
            </ConfirmModalBody>
          </ConfirmModalContent>
        </ModalOverlay>
      )}

      {showModal && paymentData && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <MdCheckCircle />
                ¡Pago Registrado Exitosamente!
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <MdClose />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <ReceiptDocument>
                <ReceiptHeader>
                  <ReceiptTitle>COMPROBANTE DE PAGO</ReceiptTitle>
                  <ReceiptSubtitle>Aether Payments Manager</ReceiptSubtitle>
                </ReceiptHeader>

                <ReceiptSection>
                  <ReceiptSectionTitle>Información del Pago</ReceiptSectionTitle>
                  <ReceiptInfo>
                    {!paymentData.pagoMultiple && (
                      <InfoItem>
                        <InfoLabel>Número de Comprobante</InfoLabel>
                        <InfoValue>{paymentData.paymentNumber}</InfoValue>
                      </InfoItem>
                    )}
                    <InfoItem>
                      <InfoLabel>Fecha y Hora</InfoLabel>
                      <InfoValue>{paymentData.timestamp}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Cliente</InfoLabel>
                      <InfoValue>{paymentData.cliente}</InfoValue>
                    </InfoItem>
                    {!paymentData.pagoMultiple && (
                      <InfoItem>
                        <InfoLabel>Recibo</InfoLabel>
                        <InfoValue>{paymentData.recibo}</InfoValue>
                      </InfoItem>
                    )}
                    <InfoItem>
                      <InfoLabel>Método de Pago</InfoLabel>
                      <InfoValue>{paymentData.metodoPago}</InfoValue>
                    </InfoItem>
                    {paymentData.numeroReferencia && (
                      <InfoItem>
                        <InfoLabel>Referencia</InfoLabel>
                        <InfoValue>{paymentData.numeroReferencia}</InfoValue>
                      </InfoItem>
                    )}
                  </ReceiptInfo>
                </ReceiptSection>

                {paymentData.pagoMultiple ? (
                  <>
                    <ReceiptSection>
                      <ReceiptSectionTitle>Recibos Pagados</ReceiptSectionTitle>
                      <ReceiptInfo>
                        {paymentData.pagosRegistrados && paymentData.pagosRegistrados.map((pago, index) => (
                          <React.Fragment key={pago.id}>
                            <InfoItem>
                              <InfoLabel>{pago.numero_recibo}</InfoLabel>
                              <InfoValue style={{ color: '#4CAF50', fontWeight: '600' }}>
                                ${pago.monto.toFixed(2)}
                              </InfoValue>
                            </InfoItem>
                          </React.Fragment>
                        ))}
                      </ReceiptInfo>
                    </ReceiptSection>

                    {paymentData.errores && paymentData.errores.length > 0 && (
                      <ReceiptSection>
                        <ReceiptSectionTitle style={{ color: '#E57373' }}>Errores</ReceiptSectionTitle>
                        <ReceiptInfo>
                          {paymentData.errores.map((error, index) => (
                            <InfoItem key={index} style={{ gridColumn: '1 / -1' }}>
                              <InfoLabel>{error.numero_recibo}</InfoLabel>
                              <InfoValue style={{ color: '#E57373', fontSize: '0.875rem' }}>
                                {error.error}
                              </InfoValue>
                            </InfoItem>
                          ))}
                        </ReceiptInfo>
                      </ReceiptSection>
                    )}

                    <ReceiptSection>
                      <TotalBox>
                        <TotalLabel>Total Pagado</TotalLabel>
                        <TotalAmount>${paymentData.totalPagado.toFixed(2)}</TotalAmount>
                      </TotalBox>
                      <div style={{ textAlign: 'center', marginTop: '12px', color: '#666', fontSize: '0.875rem' }}>
                        {paymentData.pagosRegistrados.length} recibo(s) procesado(s) exitosamente
                      </div>
                    </ReceiptSection>
                  </>
                ) : (
                  <>
                    <ReceiptSection>
                      <ReceiptSectionTitle>Detalle del Recibo</ReceiptSectionTitle>
                      <ReceiptInfo>
                        <InfoItem>
                          <InfoLabel>Descripción</InfoLabel>
                          <InfoValue>{paymentData.receiptInfo.description}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Monto Total del Recibo</InfoLabel>
                          <InfoValue>${parseFloat(paymentData.receiptInfo.monto_total || 0).toFixed(2)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Saldo Anterior</InfoLabel>
                          <InfoValue>${parseFloat(paymentData.receiptInfo.saldo_pendiente || 0).toFixed(2)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Tipo de Pago</InfoLabel>
                          <InfoValue>{paymentData.tipoPago === 'liquidar' ? 'Liquidación' : 'Abono Parcial'}</InfoValue>
                        </InfoItem>
                      </ReceiptInfo>
                    </ReceiptSection>

                    <ReceiptSection>
                      <TotalBox>
                        <TotalLabel>Monto Pagado</TotalLabel>
                        <TotalAmount>${parseFloat(paymentData.paymentAmount || 0).toFixed(2)}</TotalAmount>
                      </TotalBox>
                    </ReceiptSection>

                    {parseFloat(paymentData.newBalance || 0) > 0 && (
                      <ReceiptSection>
                        <ReceiptInfo>
                          <InfoItem style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                            <InfoLabel>Saldo Pendiente</InfoLabel>
                            <InfoValue style={{ fontSize: '1.5rem', color: '#E57373' }}>
                              ${parseFloat(paymentData.newBalance || 0).toFixed(2)}
                            </InfoValue>
                          </InfoItem>
                        </ReceiptInfo>
                      </ReceiptSection>
                    )}

                    {paymentData.newBalance === 0 && (
                      <ReceiptSection>
                        <ReceiptInfo>
                          <InfoItem style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                            <InfoValue style={{ fontSize: '1.25rem', color: '#4CAF50', fontWeight: 'bold' }}>
                              ✅ RECIBO LIQUIDADO COMPLETAMENTE
                            </InfoValue>
                          </InfoItem>
                        </ReceiptInfo>
                      </ReceiptSection>
                    )}
                  </>
                )}
              </ReceiptDocument>
            </ModalBody>

            <ModalFooter>
              <Button onClick={handlePrint}>
                <MdPrint />
                Imprimir
              </Button>
              <Button onClick={handleDownload}>
                <MdDownload />
                Descargar PDF
              </Button>
              <Button primary onClick={handleCloseModal}>
                Cerrar
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </RegisterPaymentContainer>
  );
};

export default RegisterPayment;
